import React, { forwardRef } from 'react';
import { Piece } from '../model/Piece';
import Pawn from '../piece/Pawn';
import Rook from '../piece/Rook';
import Knight from '../piece/Knight';
import Bishop from '../piece/Bishop';
import Queen from '../piece/Queen';
import King from '../piece/King';
import '../App.css';
import { Box, Tooltip } from '@mui/material';

interface CapturedPieceLegendProps {
  title: string;
  pieces: Piece[];
  pieceColor: 'white' | 'black';
  backgroundColor: string;
  isPortrait?: boolean;
  isMobile?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CapturedPiecesLegend = forwardRef<HTMLDivElement, CapturedPieceLegendProps>(
  ({ title: _title, pieces, pieceColor, backgroundColor, isPortrait = true, isMobile = false }, ref) => {
  
  // Determine piece size based on device type and orientation
  const getPieceSize = () => {
    if (isMobile) {
      if (isPortrait) {
        // Mobile portrait: consistent size
        return '32px';
      } else {
        // Mobile landscape: consistent size
        return '34px';
      }
    }
    // Desktop: consistent size
    return '38px';
  };
  
  const renderPiece = (piece: Piece) => {
    const pieceSize = getPieceSize();
    
    // Define the icon props with appropriate size constraints
    const iconProps = {
      sx: {
        width: pieceSize,
        height: pieceSize,
        maxWidth: '100%',
        maxHeight: '100%',
      }
    };
    
    switch (piece.type) {
      case 'pawn':
        return <Pawn pieceColor={pieceColor} {...iconProps} />;
      case 'rook':
        return <Rook pieceColor={pieceColor} {...iconProps} />;
      case 'knight':
        return <Knight pieceColor={pieceColor} {...iconProps} />;
      case 'bishop':
        return <Bishop pieceColor={pieceColor} {...iconProps} />;
      case 'queen':
        return <Queen pieceColor={pieceColor} {...iconProps} />;
      case 'king':
        return <King pieceColor={pieceColor} {...iconProps} />;
      default:
        return null;
    }
  };

  // Group pieces by type for compact display
  const groupedPieces = pieces.reduce((acc, piece) => {
    if (!acc[piece.type]) {
      acc[piece.type] = [];
    }
    acc[piece.type].push(piece);
    return acc;
  }, {} as Record<string, Piece[]>);

  // Determine if we should use the grouped compact display (when many pieces)
  const useCompactDisplay = isMobile && pieces.length > 7;

  return (
    <Box ref={ref} sx={{
      border: '1px solid #ccc',
      borderRadius: '1px',
      backgroundColor: backgroundColor,
      color: 'white',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      padding: isMobile 
        ? (isPortrait ? '1px' : '2px 3px') 
        : '4px', // More padding for desktop mode
      overflow: 'hidden',
      ...(isMobile && !isPortrait && {
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
      })
    }}>
      {useCompactDisplay ? (
        // Compact grouped display when many pieces are captured
        <div className={isMobile && !isPortrait 
          ? "captured-pieces-legend-mobile-landscape-compact" 
          : "captured-pieces-legend-compact"}>
          {Object.entries(groupedPieces).map(([pieceType, piecesOfType]) => (
            <Tooltip key={pieceType} title={`${pieceType.charAt(0).toUpperCase() + pieceType.slice(1)}${piecesOfType.length > 1 ? ` (${piecesOfType.length})` : ''}`}>
              <div className="piece-container-compact">
                {renderPiece(piecesOfType[0])}
                {piecesOfType.length > 1 && (
                  <div className="piece-count">{piecesOfType.length}</div>
                )}
              </div>
            </Tooltip>
          ))}
        </div>
      ) : (
        // Standard display for fewer pieces
        <div className={isMobile && !isPortrait 
          ? "captured-pieces-legend-mobile-landscape" 
          : "captured-pieces-legend"}>
          {pieces.map((piece, index) => (
            <Tooltip key={index} title={piece.type.charAt(0).toUpperCase() + piece.type.slice(1)}>
              <div className={isMobile ? "piece-container-mobile" : "piece-container"}>
                {renderPiece(piece)}
              </div>
            </Tooltip>
          ))}
        </div>
      )}
    </Box>
  );
});

export default CapturedPiecesLegend;
